<template>
  <div
    data-testid="check-details"
    class="check-details d-flex flex-column i-p-10"
  >
    <div class="title d-flex flex-row i-gap-6 i-mb-10 i-mr-12">
      <i-featured-icon
        :size="iFeaturedIconSizes.SM"
        :type="
          check.passed ? iFeaturedIconTypes.SUCCESS : iFeaturedIconTypes.ERROR
        "
        :symbol="check.passed ? 'small-check-1' : 'small-alert-triangle'"
        class="icon"
      ></i-featured-icon>
      <div class="text flex-column align-items-start flex-grow-1">
        <div class="name i-text-button-2 i-text-neutral-120">
          {{ check.name }}
        </div>
        <div class="subtitle i-text-body-3 i-text-neutral-110">
          {{ getCheckSubtitle(check) }}
        </div>
      </div>
    </div>
    <div class="requirements-list d-flex flex-column i-gap-4">
      <div
        v-for="requirement in check.requirements"
        :key="requirement.id"
        data-testid="check-requirement"
        class="requirement d-flex flex-row justify-content-start i-py-6 i-px-8 i-gap-6 i-radius-xl"
      >
        <i-icon
          :symbol="
            requirement.passed ? 'small-solid-check' : 'small-solid-delete'
          "
          :colour="
            requirement.passed
              ? palette['light-success-90']
              : palette['light-error-90']
          "
          class="icon i-mt-2"
          :size="16"
        ></i-icon>
        <div class="description d-flex flex-column">
          <div
            :class="{
              'i-text-error-120': !requirement.passed,
              'i-text-neutral-120': requirement.passed,
            }"
            class="text i-text-neutral-120"
          >
            <span class="name-label i-text-button-3">
              {{ getName(requirement.name) }}</span
            >
            <span class="operator-label i-text-neutral-110 i-text-body-3">
              {{ operatorMap[requirement.operator] || '' }}
            </span>
            <span class="rule-value i-text-button-3">
              {{ getRequiredValues(requirement) }}
            </span>
          </div>

          <div class="actual i-mt-3">
            <span class="i-text-neutral-110 i-text-body-3 i-mr-2">Actual</span
            ><span class="i-text-button-3">{{
              getActualValue(requirement)
            }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  iFeaturedIcon,
  iFeaturedIconTypes,
  iFeaturedIconSizes,
  iIcon,
} from '@inscribe/signal';
import { getCheckSubtitle, getRequirementName } from '@/utils/checks';
import {
  operatorMap,
  resolveConditionName,
  resolveConditionActualValue,
  resolveConditionValue,
} from '@/utils/conditions';
import palette from '@inscribe/signal/dist/palette';
import { required } from 'vuelidate/lib/validators';

export default {
  components: { iFeaturedIcon, iIcon },
  props: {
    check: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      iFeaturedIconTypes,
      iFeaturedIconSizes,
      operatorMap,
      palette,
    };
  },
  computed: {},
  methods: {
    getCheckSubtitle,
    resolveConditionName,
    operator() {
      return this.$store.state.config.rulesSchema.variable_type_operators[
        this.variable.field_type
      ].find(({ name }) => name === this.condition.operator);
    },
    getRequiredValues(requirement) {
      if (typeof requirement.value === 'object') {
        return requirement.value
          .map((k) => this.getValueLabel(k, requirement))
          .join(', ');
      } else {
        return requirement.value;
      }
    },
    operatorSymbol() {
      return operatorMap[this.operator.name];
    },
    getValueLabel(labelKey, requirement) {
      return requirement.name === 'text'
        ? requirement.value.join(', ')
        : resolveConditionActualValue(requirement.name, labelKey);
    },
    getActualValue(requirement) {
      const variable = this.$store.state.config.rulesSchema.variables.find(
        ({ name }) => name === requirement.name,
      );
      const operator =
        this.$store.state.config.rulesSchema.variable_type_operators[
          variable.field_type
        ].find(({ name }) => name === requirement.operator);
      let returnValue;
      if (operator.input_type !== 'none') {
        const value = ['array'].includes(typeof requirement.actual_value)
          ? requirement.actual_value?.join(', ')
          : requirement.actual_value?.toString().replace(/,/g, ', ');
        returnValue = resolveConditionValue(
          requirement.name,
          ((typeof requirement.actual_value?.length !== 'undefined' ||
            typeof requirement.actual_value === 'number') &&
            value) ||
            'None Found',
        );
      } else if (!requirement.passed && required.actual_value) {
        returnValue = resolveConditionActualValue(
          requirement.name,
          requirement.actual_value || 'None Found',
        );
      }

      return returnValue.toString();
    },
    getConfigObject(rawName) {
      return this.$store?.state?.config?.rulesSchema?.variables?.find(
        ({ name }) => name === rawName,
      );
    },
    getName(name) {
      return getRequirementName(this.$store?.state?.config?.rulesSchema, name);
    },
  },
};
</script>
<style lang="scss" scoped>
.check-details {
  .title .icon {
    min-width: 36px;
  }
  .requirement {
    border: 1px solid $light-neutral-60;

    .description {
      .rule-value {
        flex-wrap: wrap;
      }
    }

    .icon {
      min-width: 16px;
    }
  }
}
</style>
